// Service Suggestions based on Business Category
// Provides common services for each category to help users fill forms faster

import { BUSINESS_CATEGORIES, getCategoryById } from '../constants/businessCategories';

export interface ServiceSuggestion {
  id: string;
  name: string;
  category: string;
}

// Common services by category
const SERVICE_SUGGESTIONS: { [categoryId: string]: string[] } = {
  automotive: [
    'Oil Change',
    'Tire Rotation',
    'Brake Service',
    'Tire Replacement',
    'Battery Replacement',
    'AC Service',
    'Engine Diagnostic',
    'Transmission Service',
    'Car Wash',
    'Detailing',
    'State Inspection',
    'Smog Check',
  ],
  beauty_wellness: [
    'Haircut',
    'Hair Color',
    'Highlights',
    'Hair Styling',
    'Hair Treatment',
    'Manicure',
    'Pedicure',
    'Facial',
    'Massage',
    'Waxing',
    'Makeup',
    'Eyebrow Shaping',
  ],
  health_fitness: [
    'Personal Training',
    'Yoga Class',
    'Pilates Class',
    'Gym Membership',
    'Fitness Assessment',
    'Nutrition Consultation',
    'Group Fitness Class',
    'CrossFit',
    'Spinning Class',
  ],
  medical_dental: [
    'Checkup',
    'Cleaning',
    'Dental Exam',
    'Vaccination',
    'Physical Therapy',
    'Consultation',
    'Follow-up',
    'X-Ray',
    'Blood Work',
  ],
  home_garden: [
    'House Cleaning',
    'Carpet Cleaning',
    'Window Washing',
    'Plumbing Repair',
    'Electrical Work',
    'HVAC Service',
    'Landscaping',
    'Lawn Mowing',
    'Handyman Service',
  ],
  pet_care: [
    'Pet Grooming',
    'Vet Visit',
    'Vaccination',
    'Pet Boarding',
    'Dog Walking',
    'Pet Training',
    'Pet Sitting',
    'Nail Trimming',
  ],
  technology: [
    'Computer Repair',
    'Phone Repair',
    'Data Recovery',
    'IT Support',
    'Network Setup',
    'Software Installation',
    'Virus Removal',
    'Hardware Upgrade',
  ],
  food_dining: [
    'Restaurant Reservation',
    'Catering',
    'Private Chef',
    'Meal Prep',
    'Cooking Class',
  ],
  education: [
    'Tutoring',
    'Test Prep',
    'Music Lessons',
    'Language Class',
    'Math Tutoring',
    'Science Tutoring',
  ],
  legal: [
    'Legal Consultation',
    'Notary Service',
    'Contract Review',
    'Estate Planning',
    'Immigration Consultation',
  ],
  financial: [
    'Tax Preparation',
    'Accounting Service',
    'Financial Planning',
    'Bookkeeping',
    'Tax Consultation',
  ],
  real_estate: [
    'Property Viewing',
    'Home Inspection',
    'Appraisal',
    'Consultation',
    'Contract Review',
  ],
  event_planning: [
    'Event Planning',
    'Wedding Planning',
    'Party Planning',
    'Corporate Event',
    'Consultation',
  ],
  photography: [
    'Event Photography',
    'Portrait Session',
    'Headshot',
    'Event Photography',
    'Product Photography',
  ],
  construction: [
    'Remodeling',
    'Renovation',
    'Painting',
    'Flooring',
    'Roofing',
    'Deck Building',
  ],
  moving_storage: [
    'Moving Service',
    'Packing Service',
    'Storage',
    'Junk Removal',
    'Hauling',
  ],
  transportation: [
    'Taxi Service',
    'Ride Share',
    'Delivery',
    'Airport Shuttle',
    'Limousine',
  ],
  childcare: [
    'Daycare',
    'Babysitting',
    'Nanny Service',
    'Kids Party',
    'Activity Center',
  ],
  security_safety: [
    'Security System',
    'Locksmith',
    'Home Inspection',
    'Pest Control',
    'Alarm Service',
  ],
  digital_marketing: [
    'Social Media Management',
    'SEO Service',
    'Digital Ads',
    'Content Creation',
    'Web Design',
  ],
  senior_care: [
    'Home Health Aide',
    'Senior Transportation',
    'Companion Care',
    'Assisted Living',
    'Nursing Care',
  ],
  sports_coaching: [
    'Tennis Lessons',
    'Golf Lessons',
    'Swim Lessons',
    'Sports Coaching',
    'Athletic Training',
  ],
  personal_styling: [
    'Personal Shopping',
    'Wardrobe Styling',
    'Image Consulting',
    'Fashion Advice',
    'Closet Organization',
  ],
  halal: [
    'Halal Food',
    'Halal Restaurant',
    'Halal Catering',
    'Halal Meat',
    'Halal Grocery',
  ],
};

/**
 * Get service suggestions for a business category
 */
export const getServiceSuggestions = (categoryId: string): string[] => {
  // Normalize category ID (handle variations)
  const normalizedCategory = categoryId.toLowerCase().replace(/\s+/g, '_');
  
  // Direct match
  if (SERVICE_SUGGESTIONS[normalizedCategory]) {
    return SERVICE_SUGGESTIONS[normalizedCategory];
  }
  
  // Try to find by category name
  const category = getCategoryById(normalizedCategory);
  if (category) {
    // Extract common services from description
    const descriptionServices = category.description
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
    
    return descriptionServices;
  }
  
  // Fallback: return generic services
  return [
    'Consultation',
    'Service',
    'Appointment',
  ];
};

/**
 * Search services by query (for auto-complete)
 */
export const searchServices = (query: string, categoryId?: string): string[] => {
  const lowerQuery = query.toLowerCase();
  
  // If category specified, search within that category
  if (categoryId) {
    const suggestions = getServiceSuggestions(categoryId);
    return suggestions.filter(service => 
      service.toLowerCase().includes(lowerQuery)
    );
  }
  
  // Search across all categories
  const allServices: string[] = [];
  Object.values(SERVICE_SUGGESTIONS).forEach(services => {
    allServices.push(...services);
  });
  
  // Remove duplicates and filter
  const uniqueServices = Array.from(new Set(allServices));
  return uniqueServices
    .filter(service => service.toLowerCase().includes(lowerQuery))
    .slice(0, 10); // Limit to 10 results
};

/**
 * Get popular services (most common across categories)
 */
export const getPopularServices = (): string[] => {
  return [
    'Consultation',
    'Service',
    'Appointment',
    'Checkup',
    'Repair',
    'Cleaning',
    'Installation',
  ];
};



